import React, { useState, useEffect, useRef } from 'react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  disabled?: boolean;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({ onRefresh, children, disabled = false }) => {
  const [startY, setStartY] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isKicked, setIsKicked] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPWA, setIsPWA] = useState(false);
  
  const REFRESH_THRESHOLD = 90;
  const MAX_PULL_DISTANCE = 180;

  // Determine whether app is running as a PWA (standalone) including iOS
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const checkPwa = () => {
      const isPwaDisplay = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
      const isIosPwa = (window.navigator as any).standalone === true;
      setIsPWA(Boolean(isPwaDisplay || isIosPwa));
    };

    checkPwa();

    // listen for changes to display-mode where supported
    let mql: MediaQueryList | null = null;
    try {
      if (window.matchMedia) {
        mql = window.matchMedia('(display-mode: standalone)');
        if (mql && typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', checkPwa);
        } else if (mql && (mql as any).addListener) {
          (mql as any).addListener(checkPwa);
        }
      }
    } catch (e) {
      // ignore
    }

    return () => {
      if (mql) {
        if (typeof mql.removeEventListener === 'function') {
          mql.removeEventListener('change', checkPwa);
        } else if ((mql as any).removeListener) {
          (mql as any).removeListener(checkPwa);
        }
      }
    };
  }, []);

  const enabled = isPWA && !disabled;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!enabled || refreshing) return;

    const container = containerRef.current;
    if (container && container.scrollTop === 0) {
      setStartY(e.touches[0].pageY);
      setIsKicked(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!enabled || refreshing || startY === null) return;

    const currentY = e.touches[0].pageY;
    const diff = currentY - startY;

    if (diff > 0) {
      // Improved resistance for better feel
      const distance = Math.min(Math.pow(diff, 0.85) * 1.5, MAX_PULL_DISTANCE);
      setPullDistance(distance);
    } else {
      setPullDistance(0);
    }
  };

  const handleTouchEnd = async () => {
    if (!enabled || refreshing || startY === null) return;

    if (pullDistance >= REFRESH_THRESHOLD) {
      setIsKicked(true);
      setRefreshing(true);
      
      // Slow and Dramatic kick motion delay (1 second)
      setTimeout(async () => {
        try {
          await onRefresh();
        } catch (error) {
          console.error('Refresh failed:', error);
        } finally {
          // Stay in refresh state for a bit more for smooth feel
          setTimeout(() => {
            setRefreshing(false);
            setPullDistance(0);
            setIsKicked(false);
          }, 1200);
        }
      }, 800);
    } else {
      setPullDistance(0);
    }
    setStartY(null);
  };

  const FootballIcon = ({ className = "" }: { className?: string }) => (
    <div className={`relative ${className}`}>
      <img 
        src="/assets/ball.png" 
        alt="Football"
        className="w-full h-full object-contain"
      />
    </div>
  );

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-y-auto overflow-x-hidden"
      onTouchStart={enabled ? handleTouchStart : undefined}
      onTouchMove={enabled ? handleTouchMove : undefined}
      onTouchEnd={enabled ? handleTouchEnd : undefined}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {/* High Visibility Animation Layer (only rendered when running as PWA) */}
      {isPWA && (
      <div 
        className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none overflow-visible"
        style={{ 
          height: '100px',
          top: '-100px',
          transform: `translateY(${pullDistance}px)`,
          transition: refreshing ? 'transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.1)' : 'none',
          zIndex: 100
        }}
      >
        <div className="relative w-24 h-24 flex items-center justify-center">
          {/* Background Highlight for visibility */}
          <div 
            className="absolute inset-0 bg-blue-500/15 rounded-full blur-2xl transition-opacity duration-500"
            style={{ opacity: pullDistance / REFRESH_THRESHOLD }}
          />

          {/* Football with enhanced motions */}
          <div 
            className={`w-12 h-12 relative z-10 transition-all duration-500 ${
              refreshing && !isKicked ? 'animate-slow-bounce' : ''
            } ${isKicked ? 'animate-dramatic-kick' : ''}`}
            style={{ 
              transform: !refreshing 
                ? `rotate(${pullDistance * 5}deg) scale(${0.4 + (pullDistance/REFRESH_THRESHOLD) * 0.9})` 
                : 'none',
            }}
          >
            <FootballIcon className="w-full h-full" />
          </div>

          {/* Spinner Ring */}
          {refreshing && !isKicked && (
            <div className="absolute -inset-3 flex items-center justify-center">
               <div className="w-20 h-20 border-[4px] border-blue-600/80 border-t-transparent rounded-full animate-spin shadow-xl"></div>
            </div>
          )}
          
          {/* Status Text */}
          <div className="absolute -bottom-12 flex flex-col items-center gap-1">
            {pullDistance > 20 && !refreshing && (
              <div 
                className={`font-black italic text-sm tracking-tighter transition-all duration-300 ${
                  pullDistance >= REFRESH_THRESHOLD ? 'text-blue-600 scale-125' : 'text-gray-400'
                }`}
              >
              </div>
            )}
            {refreshing && !isKicked && (
              <div className="text-blue-600 font-bold animate-pulse text-xs uppercase tracking-widest mt-2">
                Analyzing Match...
              </div>
            )}
          </div>
        </div>
      </div>
      )}
      
      {/* Content Layer with smoother transition */}
      <div 
        className="h-full w-full"
        style={{ 
          transform: refreshing ? `translateY(${refreshing ? 120 : pullDistance * 0.45}px)` : 'none',
          transition: refreshing ? 'transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.1)' : 'none'
        }}
      >
        {children}
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes dramatic-kick {
          0% { transform: scale(1) translateY(0); opacity: 1; filter: blur(0); }
          15% { transform: scale(0.85) translateY(10px); opacity: 1; }
          100% { transform: scale(4) translateY(-200px); opacity: 0; filter: blur(10px); }
        }
        @keyframes slow-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        .animate-dramatic-kick {
          animation: dramatic-kick 1.2s forwards cubic-bezier(0.5, 0, 0.75, 0);
        }
        .animate-slow-bounce {
          animation: slow-bounce 1.5s ease-in-out infinite;
        }
      `}} />
    </div>
  );
};

export default PullToRefresh;
